'use client';
import React, { useState } from 'react';
import Image from 'next/image';

interface ProductImageFallbackProps {
  src: string | null | undefined;
  alt: string;
  fill?: boolean;
  className?: string;
  sizes?: string;
  priority?: boolean;
  containerClassName?: string;
}

function PlaceholderIcon({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 w-full h-full text-white/20">
      <svg
        className="w-16 h-16"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Circuit board / industrial automation icon */}
        <rect x="8" y="8" width="48" height="48" rx="4" stroke="currentColor" strokeWidth="1.5" />
        {/* CPU chip */}
        <rect x="20" y="20" width="24" height="24" rx="2" stroke="currentColor" strokeWidth="1.5" />
        {/* Pins left */}
        <line x1="8" y1="26" x2="20" y2="26" stroke="currentColor" strokeWidth="1.5" />
        <line x1="8" y1="32" x2="20" y2="32" stroke="currentColor" strokeWidth="1.5" />
        <line x1="8" y1="38" x2="20" y2="38" stroke="currentColor" strokeWidth="1.5" />
        {/* Pins right */}
        <line x1="44" y1="26" x2="56" y2="26" stroke="currentColor" strokeWidth="1.5" />
        <line x1="44" y1="32" x2="56" y2="32" stroke="currentColor" strokeWidth="1.5" />
        <line x1="44" y1="38" x2="56" y2="38" stroke="currentColor" strokeWidth="1.5" />
        {/* Pins top */}
        <line x1="26" y1="8" x2="26" y2="20" stroke="currentColor" strokeWidth="1.5" />
        <line x1="32" y1="8" x2="32" y2="20" stroke="currentColor" strokeWidth="1.5" />
        <line x1="38" y1="8" x2="38" y2="20" stroke="currentColor" strokeWidth="1.5" />
        {/* Pins bottom */}
        <line x1="26" y1="44" x2="26" y2="56" stroke="currentColor" strokeWidth="1.5" />
        <line x1="32" y1="44" x2="32" y2="56" stroke="currentColor" strokeWidth="1.5" />
        <line x1="38" y1="44" x2="38" y2="56" stroke="currentColor" strokeWidth="1.5" />
        {/* Inner dots */}
        <circle cx="28" cy="28" r="2" fill="currentColor" />
        <circle cx="36" cy="28" r="2" fill="currentColor" />
        <circle cx="32" cy="36" r="2" fill="currentColor" />
      </svg>
      {label && (
        <span className="text-xs font-medium tracking-wide text-white/20">{label}</span>
      )}
    </div>
  );
}

export default function ProductImageFallback({
  src,
  alt,
  fill = true,
  className = '',
  sizes,
  priority = false,
}: ProductImageFallbackProps) {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const showImage = src && !imgError;

  return (
    <>
      {/* Placeholder — always rendered, hidden once image loads */}
      {(!showImage || !imgLoaded) && (
        <div className="absolute inset-0 flex items-center justify-center">
          {!showImage ? (
            <PlaceholderIcon label="No Image Available" />
          ) : (
            /* Loading shimmer */
            <div className="absolute inset-0 bg-white/5 animate-pulse" />
          )}
        </div>
      )}

      {showImage && (
        <Image
          src={src}
          alt={alt}
          fill={fill}
          className={`${className} transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          sizes={sizes}
          priority={priority}
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgError(true)}
          unoptimized
        />
      )}
    </>
  );
}
